import type { IBracketStore, } from "@store/types";
import { produce } from 'immer';
import type { StateCreator, } from "zustand";
import type { Store } from '../useStore';
import type { MessageHandler, } from "@bracket/rabbit";
import * as G from "@globals";
import * as R from "@bracket/rabbit/mqtt";
import type { EthereumAddress, } from "@bracket/types";
import type { ListenerArray, ListenerType,INotificationsSlice, } from "./types";
import { MQNotifications, } from "./mq";



export const createNotificationsSlice: StateCreator<IBracketStore, [], [], INotificationsSlice> = ( set, get, ) => {


	const fetch = async () => {


		const { ready, fetching, } = get().notificationSlice;
		if(ready || fetching) {
			return;
		}
		
		set(produce<Store>(state => {
			state.notificationSlice.fetching = true;
		}));

		const { address } = get().wallet;
		const { addNotification } = get().notificationSlice;
		const notified: string[] = [];

		// AppKit and Wagmi hate each other enough to bomb the sockets so we wait until the tantrum is over.
		setTimeout(async () => {

			_disconnector = await R.subscribe(
				[
					"wallets.all",
					`wallets.${address}`
				],
				(msg) => {
					// @ts-ignore MessageHandler should be typed with a generic
					const hash = msg?.data?.transaction_hash ? msg.data.transaction_hash : null;
					if(hash && notified.includes(hash)) {
						return;
					}
					notified.push(hash);
					if(typeof msg.type === "string" && msg.type in MQNotifications) {
						// @ts-ignore MessageHandler should be typed with a generic
						const notification = MQNotifications[msg.type as string](msg.data);
						( async () => addNotification(notification) )();
						return;
					}
				},
			);

			set(produce<Store>(state => {
				state.notificationSlice.connected = true;
				state.notificationSlice.ready = true;
				state.notificationSlice.fetching = false;
			}));

		}, 2000);

		set(produce<Store>(state => {
			state.ui.ready = true;
			state.ui.fetching = false;
		}));
	}

	const fetchNotifications = async () => {
		const { notifications, } = await gql("LOAD_NOTIFICATIONS") as { notifications: Notification[]; };
		set(produce<Store>(state => {
			state.notificationSlice.notifications = notifications;
		}));
	};

	const onWalletNotification = ( fn: MessageHandler ) => {
		const { address, } = G.wallet.getBracketWallet();
		if(!address) {
			throw new Error("Notifications slice invoked onWalletNotification without an address present");
		}
		R.subscribe(`wallets.${address?.toLowerCase()}`, fn).then((unsubscribe) => {
			const next = get().notificationSlice.wallet_listeners.slice();
			set(produce<Store>(state => {
				next.push({ unsubscribe, fn });
				state.notificationSlice.wallet_listeners = next;
			}));
		});
	};


	const onBroadcastNotification =  ( fn: MessageHandler ) => {
		R.subscribe("wallets.all", fn).then((unsubscribe) => {
			const next = get().notificationSlice.broadcast_listeners.slice();
			set(produce<Store>(state => {
				next.push({ unsubscribe, fn });
				state.notificationSlice.broadcast_listeners = next;
			}));
		});
	};


	const onVaultNotification = ( address: EthereumAddress, fn: MessageHandler ) => {
		R.subscribe(`vaults.${address.toLowerCase()}`, fn).then((unsubscribe) => {
			const next = get().notificationSlice.vault_listeners.slice();
			set(produce<Store>(state => {
				next.push({ unsubscribe, fn, });
				state.notificationSlice.vault_listeners = next;
			}));
		});
	};


	const clear = (type: ListenerType, el: MessageHandler|"ALL") => {
		const listeners: ListenerArray = [];
		switch(type) {
			case "WALLET": {
				listeners.push(...get().notificationSlice.wallet_listeners);
				break;
			}
			case "BROADCAST": {
				listeners.push(...get().notificationSlice.broadcast_listeners);
				break;
			}
			case "VAULT": {
				listeners.push(...get().notificationSlice.vault_listeners);
				break;
			}
			default: 
				throw new Error(`notificationsSlice::clear was invoked with unknown type "${type}"`);
		}

		const garbage: ListenerArray = [];
		for(const ln of listeners) {
			if(el === "ALL" || ln.fn === el) {
				ln.unsubscribe();
				garbage.push(ln);
			}
		}

		// build the next version of the listener array
		const next = listeners.filter((ln) => !garbage.includes(ln));

		switch(type) {
			case "WALLET": {
				set(produce<Store>(state => { state.notificationSlice.wallet_listeners = next }));
				break;
			}
			case "BROADCAST": {
				set(produce<Store>(state => { state.notificationSlice.broadcast_listeners = next }));
				break;
			}
			case "VAULT": {
				set(produce<Store>(state => { state.notificationSlice.vault_listeners = next }));
				break;
			}
			default: 
				throw new Error(`notificationsSlice::clear was invoked with unknown type "${type}"`);
		}
	};


	const addNotification = ( notification: Omit<Notification, "id">) => {
		const id = Date.now().toString();
		set(produce<Store>(state => {
			state.notificationSlice.notifications.push({ ...notification, id, });
		}));
		// Automatically remove the notification after 5 seconds if it still exists
		setTimeout(() => {
			const currentNotifications = get().notificationSlice.notifications;
			if (currentNotifications.some((n) => n.id === id)) {
				get().notificationSlice.removeNotification(id);
			}
		}, 5000);
	};


	const removeNotification =  (id: string) => {
		set(produce<Store>(state => {
			state.notificationSlice.notifications =
				state.notificationSlice.notifications.filter((n) => n.id !== id);
		}));
	};


	const reset = () => {
		console.warn("store:ui::reset:: not implemented");
	};


	const resetAsync = async () => {
		console.warn("store:ui::resetAsync:: not implemented");
	};


	const setMenuState = ( next: "OPEN" | "CLOSED" ) => {
		set(produce<Store>(state => {
			state.ui.menuState = next;
		}));
	};



	const _setDefaults = () => {
		set(produce<Store>(state => {
			state.ui.menuState = "CLOSED";
		}));
	};

	// place at the bottom of the call stack.
	// setTimeout(_ => fetch(), 0);

	let _disconnector: Awaited<ReturnType<typeof R.subscribe>> | null = null;

	
	return {
		ready: false,
		fetching: false,
		notifications: [],
		listeners: [],
		wallet_listeners: [],
		vault_listeners: [],
		broadcast_listeners: [],
		connected: false,
		addNotification,
		clear,
		fetch,
		onBroadcastNotification,
		onVaultNotification,
		onWalletNotification,
		removeNotification,
		reset,
		resetAsync,
		setMenuState,
	};

};
